import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronRight, 
  Sparkles, 
  ArrowUpRight, 
  ArrowRight, 
  PlayCircle, 
  BookOpen, 
  Target, 
  Clock, 
  Flame, 
  Layers, 
  Calendar, 
  Zap, 
  Pin, 
  Lightbulb, 
  X, 
  Moon, 
  RotateCcw, 
  AlertCircle, 
  Brain,
  Heart,
  TrendingUp,
  Grid,
  FileText,
  Image as ImageIcon,
  Compass,
  Star,
  Palette
} from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import { FLAGS } from '../lib/flags';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { loadDashboardData } from '../services/dashboardDataService';
import { telemetry } from '../lib/telemetry';
import { useBehavioralLearning } from '../hooks/useBehavioralLearning';
import { getImageUrl } from '../utils/imageProxy';
import { getThumbnailUrl } from '../utils/thumbnailFallbacks';
import openaiService from '../services/openaiService';
import ContentDetailModal from '../components/modals/ContentDetailModal';
import SmartSuggestionModal from '../components/modals/SmartSuggestionModal';
import contentService from '../services/contentService';
import personalizationService from '../services/personalizationService';
import { classifyTheme, getDominantTheme } from '../lib/themeClassifier';
import { THEME_MICRO_SUMMARIES } from '../lib/themeKeywords';
import suggestionsService from '../services/suggestionsService';
import {
  getCachedWeeklySummary,
  isCacheValid,
  shouldRegenerateSummary,
  saveWeeklySummaryCache,
  getSummaryFromCache
} from '../services/weeklySummaryCacheService';
import HomeHeader from './Home/HomeHeader';
import WeeklySummary from './Home/WeeklySummary';
import Momentum from './Home/Momentum';
import ContinueThemes from './Home/ContinueThemes';
import SmartInsights from './Home/SmartInsights';
import MemorySurfacing from './Home/MemorySurfacing';
import RecentlySaved from './Home/RecentlySaved';
import Focuses from './Home/Focuses';
import Plans from './Home/Plans';
import { documentService } from '../services/documentService';

// Simple AI text generation helper
const ai = {
  async text(prompt, systemPrompt) {
    try {
      const response = await openaiService.createChatCompletion([
        {
          role: 'system',
          content: systemPrompt || 'You are Swirl, a personal insights narrator. Use a warm, clear, observational tone.'
        },
        {
          role: 'user',
          content: prompt
        }
      ], {
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 400 // Increased for more detailed summaries
      });

      if (response.error || !response.data) {
        throw new Error(response.error || 'AI generation failed');
      }

      // API endpoint returns content directly in response.data (not full OpenAI response)
      const content = response.data;
      if (!content) {
        throw new Error('No content in AI response');
      }

      return content;
    } catch (error) {
      console.error('AI text generation failed:', error);
      throw error;
    }
  }
};

// ---------- Weekly Summary Helpers (deterministic branches) ----------

// Basic keyword match on title + tags for fashion-oriented items
function isFashionItem(item) {
  const title = (item.title || '').toLowerCase();
  const tags = (item.auto_tags?.topics || item.auto_tags?.themes || [])
    .map((t) => String(t).toLowerCase());
  const words = `${title} ${tags.join(' ')}`;

  const fashionKeywords = [
    'suit',
    'blazer',
    'coat',
    'jacket',
    'trench',
    'tailored',
    'formal',
    'dress',
    'gown',
    'outfit',
    'lookbook',
    'look',
    'style',
    'stylish',
  ];

  return fashionKeywords.some((k) => words.includes(k));
}

// City / setting signals (stairs, railings, urban backdrops, etc.)
function isCityOrSettingItem(item) {
  const title = (item.title || '').toLowerCase();
  const tags = (item.auto_tags?.topics || item.auto_tags?.themes || [])
    .map((t) => String(t).toLowerCase());
  const words = `${title} ${tags.join(' ')}`;

  const settingKeywords = [
    'city',
    'urban',
    'street',
    'downtown',
    'stairs',
    'steps',
    'staircase',
    'rail',
    'railing',
    'architecture',
    'cityscape',
    'london',
    'paris',
    'tokyo',
    'new york',
  ];

  return settingKeywords.some((k) => words.includes(k));
}

function getSeasonLabel(date = new Date()) {
  const m = date.getMonth(); // 0-based
  if (m === 11 || m === 0 || m === 1) return 'winter';
  if (m >= 2 && m <= 4) return 'spring';
  if (m >= 5 && m <= 7) return 'summer';
  return 'fall';
}

// Deterministic summary when user is clearly shopping a sharper winter city look
function buildWinterCityLookSummary({ recentItems, lateSaves }) {
  const fashionItems = recentItems.filter(isFashionItem);
  const cityItems = recentItems.filter(isCityOrSettingItem);
  const season = getSeasonLabel();
  const monthName = new Date().toLocaleString('default', { month: 'long' });

  const exampleTitles = fashionItems
    .slice(0, 3)
    .map((i) => i.title)
    .filter(Boolean);

  const examplesText = exampleTitles.length
    ? exampleTitles.map((t) => `"${t}"`).join(', ')
    : null;

  let settingPhrase = '';
  if (cityItems.length && fashionItems.length) {
    settingPhrase = 'in city streets and on steps / railings';
  } else if (cityItems.length) {
    settingPhrase = 'in urban backdrops';
  } else {
    settingPhrase = 'in everyday outdoor settings';
  }

  const teaser = 'Shopping a sharper winter city look';

  const summaryParts = [];

  // Sentence 1: concrete items + setting
  if (examplesText) {
    summaryParts.push(
      `Your recent saves cluster around polished outfits—${examplesText}—tried out ${settingPhrase}.`
    );
  } else {
    summaryParts.push(
      `Your recent saves cluster around polished outfits—formal pieces and structured looks—tried out ${settingPhrase}.`
    );
  }

  // Sentence 2: interpretation as a uniform / shopping behavior
  summaryParts.push(
    `The pieces sit in a tight band of rich tones and clean neutrals, which is what shopping looks like when you're converging on one ${season} city uniform rather than browsing at random.`
  );

  // Sentence 3: timing + behavior
  const behaviorBit =
    lateSaves >= 4
      ? 'With most of this happening at night, it reads like end-of-day work on how you want to show up in photos and in person.'
      : `Landing in ${monthName}, it feels like quiet prep for winter events and city moments where you want to look sharper than your baseline.`;
  summaryParts.push(behaviorBit);

  const summary = summaryParts.join(' ');

  const suggestions = [
    'Drop your strongest suit and coat saves into a Focus called “Winter City Uniform”.',
    'Ask Swirl to pull out the common details (fits, colors, shapes) from that Focus and turn them into a simple wardrobe checklist.',
    'Add a couple of locations that match this look so outfits and settings are designed together.',
  ];

  return { teaser, summary, suggestions };
}

// Weekly Summary AI generator - High-signal, specific summaries with robust fallback logic
async function generateAISummary({ topTopics = [], weeklyCount = 0, topicMap = {}, dominantTheme = 'General', items = [] }) {
  try {
    // Get actual item titles, tags, descriptions, timestamps to work with REAL data
    const recentItems = (items || [])
      .slice(0, 30)
      .map(item => ({
        title: item.title || '',
        tags: item.auto_tags?.topics || item.auto_tags?.themes || [],
        description: item.description || '',
        created_at: item.created_at,
        url: item.url || '',
        source_type: item.source_type || ''
      }))
      .filter(item => item.title || item.tags.length > 0);

    // Only show "no items" if we truly have no activity
    if (weeklyCount === 0 && recentItems.length === 0 && topTopics.length === 0) {
      return {
        teaser: 'Start saving items to see your weekly summary',
        summary: 'Save a few items and your weekly patterns will start to emerge. Swirl analyzes your saves to surface meaningful insights.',
        suggestions: [
          'Save items that catch your attention this week',
          'Use the browser extension to capture content quickly',
          'Try saving from different sources to see patterns form'
        ]
      };
    }
    
    // If we have weeklyCount or topTopics but no item details, use topic-based summary
    if (recentItems.length === 0 && (weeklyCount > 0 || topTopics.length > 0)) {
      const topicsStr = topTopics.slice(0, 3).join(', ') || dominantTheme;
      return {
        teaser: topTopics[0] ? `Deep into ${topTopics[0].toLowerCase()} this week` : 'Patterns emerging',
        summary: `${topicsStr} stood out this week. Your saves are forming a clear direction—this looks like intentional focus, not random browsing.`,
        suggestions: [
          topTopics[0] ? `Create a Focus around ${topTopics[0].toLowerCase()}` : 'Group your recent saves into a Focus',
          'Review your saves to see what connects them',
          'Save a few more items to sharpen the pattern'
        ]
      };
    }

    // Extract concrete topics from actual items
    const topicCounts = {};
    recentItems.forEach(item => {
      item.tags.forEach(tag => {
        topicCounts[tag] = (topicCounts[tag] || 0) + 1;
      });
    });

    const topActualTopics = Object.entries(topicCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([topic, count]) => `${topic} (${count} items)`);

    // FALLBACK LEVEL 1: Analyze micro-signals (formats, verbs, tones, metadata)
    const formatCounts = {};
    const domainCounts = {};
    const sourceTypeCounts = {};
    recentItems.forEach(item => {
      if (item.source_type) {
        sourceTypeCounts[item.source_type] = (sourceTypeCounts[item.source_type] || 0) + 1;
      }
      if (item.url) {
        try {
          const domain = new URL(item.url).hostname.replace('www.', '');
          domainCounts[domain] = (domainCounts[domain] || 0) + 1;
        } catch {}
      }
    });

    // FALLBACK LEVEL 2: Analyze behavioral patterns (timing, bursts, frequency)
    const hourCounts = {};
    const dayCounts = {};
    let lateSaves = 0;
    let earlySaves = 0;
    recentItems.forEach(item => {
      const date = new Date(item.created_at);
      const hour = date.getHours();
      const day = date.getDay();
      hourCounts[hour] = (hourCounts[hour] || 0) + 1;
      dayCounts[day] = (dayCounts[day] || 0) + 1;
      if (hour >= 22 || hour < 5) lateSaves++;
      if (hour >= 5 && hour < 8) earlySaves++;
    });

    // Determine fallback level needed
    const strongestTopicCount = topActualTopics[0] ? parseInt(topActualTopics[0].split('(')[1]) : 0;
    const hasStrongCluster = strongestTopicCount >= 5;
    const hasModerateCluster = strongestTopicCount >= 3;
    const topFormat = Object.entries(sourceTypeCounts).sort((a, b) => b[1] - a[1])[0];
    const topDomain = Object.entries(domainCounts).sort((a, b) => b[1] - a[1])[0];

    const strongest = topActualTopics[0]?.split(' (')[0] || dominantTheme;
    const secondTopic = topActualTopics[1]?.split(' (')[0];

    // ----- Deterministic “winter city look” branch -----
    const fashionItems = recentItems.filter(isFashionItem);
    const fashionRatio = recentItems.length
      ? fashionItems.length / recentItems.length
      : 0;
    const season = getSeasonLabel();

    // If clearly a winter fashion-heavy week, skip AI and use deterministic summary
    if (season === 'winter' && fashionRatio >= 0.4) {
      const { teaser, summary, suggestions } = buildWinterCityLookSummary({
        recentItems,
        lateSaves,
      });

      if (import.meta.env?.DEV) {
        console.log('✅ Using deterministic winter city look summary branch');
      }

      return { teaser, summary, suggestions };
    }

    // Build context for AI with fallback signals
    let contextType = 'STRONG_CLUSTER';
    let fallbackContext = '';
    
    if (!hasStrongCluster && !hasModerateCluster) {
      contextType = 'MICRO_SIGNALS';
      fallbackContext = `
FALLBACK MODE: Weak topic clusters detected. Analyze micro-signals instead:
- Top format: ${topFormat ? `${topFormat[0]} (${topFormat[1]} items)` : 'mixed'}
- Top domain: ${topDomain ? `${topDomain[0]} (${topDomain[1]} saves)` : 'various'}
- Late-night saves (10pm-5am): ${lateSaves}
- Early morning saves (5am-8am): ${earlySaves}
- Behavioral pattern needed: Look for format clusters, timing patterns, source patterns`;
    } else if (!hasStrongCluster) {
      contextType = 'BEHAVIORAL_PATTERNS';
      fallbackContext = `
FALLBACK MODE: Moderate clusters. Include behavioral context:
- Late-night saves: ${lateSaves}
- Early morning saves: ${earlySaves}
- Look for timing patterns and saving bursts`;
    }

    // Extract subtopic patterns from titles (not just tags)
    const titleWords = {};
    recentItems.forEach(item => {
      const words = (item.title || '').toLowerCase()
        .split(/[\s,\-–—:;|•·]+/)
        .filter(w => w.length > 3 && !['with', 'from', 'that', 'this', 'your', 'what', 'when', 'where', 'which', 'their', 'there', 'here', 'have', 'been', 'will', 'would', 'could', 'should', 'about', 'into', 'over', 'after', 'before', 'between', 'under', 'through', 'during', 'without', 'within', 'along', 'following', 'across', 'behind', 'beyond', 'plus', 'except', 'http', 'https', 'www'].includes(w));
      words.forEach(word => {
        titleWords[word] = (titleWords[word] || 0) + 1;
      });
    });
    
    const topTitlePatterns = Object.entries(titleWords)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .filter(([word, count]) => count >= 2)
      .map(([word, count]) => `"${word}" (${count}x)`);

    // Build teaser - use specific recurring title words, NEVER counts
    const rawTopWord = topTitlePatterns[0]?.replace(/["\(\)0-9x]/g, '').trim() || '';
    const rawSecondWord = topTitlePatterns[1]?.replace(/["\(\)0-9x]/g, '').trim() || '';

    const cap = (word) => word ? word.charAt(0).toUpperCase() + word.slice(1) : '';
    const topWord = cap(rawTopWord);
    const teaserSecondWord = cap(rawSecondWord);
    
    const teaser = topWord && teaserSecondWord
      ? `${topWord} and ${teaserSecondWord} dominated this week`
      : topWord
        ? `Deep into ${topWord} this week`
        : strongest
          ? `Focused on ${strongest.toLowerCase()} this week`
          : 'Your patterns are forming';

    // Build item list with full context
    const itemList = recentItems.slice(0, 15).map((item, i) => 
      `${i + 1}. "${item.title}"${item.tags.length > 0 ? ` [${item.tags.slice(0, 4).join(', ')}]` : ''}`
    ).join('\n');

    // Summary prompt - ITERATION 1: Focus on specificity and cross-cluster connections
    const summaryPrompt = `You are analyzing a user's saved items to write a weekly summary. Your job is to be SPECIFIC and INSIGHTFUL.

=== RAW DATA ===
SAVED ITEMS (${recentItems.length} total):
${itemList}

TOP TAGS: ${topActualTopics.join(', ') || 'none dominant'}
RECURRING TITLE WORDS: ${topTitlePatterns.join(', ') || 'no strong patterns'}
BEHAVIORAL: ${lateSaves > 3 ? `${lateSaves} late-night saves (10pm-5am)` : ''}${earlySaves > 3 ? `, ${earlySaves} early saves (5-8am)` : ''}
TOP SOURCE: ${topDomain ? `${topDomain[0]} (${topDomain[1]} saves)` : 'various'}

=== YOUR TASK ===
Write a 2-3 sentence summary that:
1. Names SPECIFIC subtopics from the titles (e.g., "structured blazers", "kettlebell swings", "Kyoto temples") - NOT generic categories
2. Connects clusters if there's overlap (e.g., "fashion + travel saves = trip wardrobe planning")
3. Interprets intent: What are they building toward? A trip? A project? A skill? A style shift?
4. If clusters are weak, comment on behavioral patterns (timing, sources, formats)

=== HARD REQUIREMENTS ===
- First sentence: State the dominant micro-pattern with specific language from titles
- Second sentence: Interpret what this collection suggests (goal, project, shift)
- Third sentence (optional): Note any interesting cross-connections or behavioral signals

=== ABSOLUTE BANS (instant fail if used) ===
- NEVER say "you saved X items" or "X saves" or any item count
- NEVER say "this week you..." or "your week..."
- NEVER use: "across various", "different topics", "broad range", "general interest", "explored", "engaged with", "delved into", "journey", "vibrant", "harmonious", "blend", "tapestry", "curiosity led", "stayed curious", "productive week", "explore additional", "diversify", "broader context", "collection suggests", "indicates an interest"
- NEVER start with a count or statistic
- NEVER summarize what they did - INTERPRET what it means

=== REQUIRED STRUCTURE ===
- First sentence: State the SPECIFIC micro-pattern you see (e.g., "Red suits, structured blazers, and evening formal wear")
- Second sentence: WHY this matters - what are they building toward? (trip, event, style shift, project)
- Third sentence: A SPECIFIC insight that connects dots they might not see

=== SUGGESTION RULES ===
- NEVER suggest "explore more sources" or "diversify inspiration"
- EVERY suggestion must reference a SPECIFIC item title, tag, or visual pattern
- Example good: "Build a capsule wardrobe board from the red suit and structured blazer saves"
- Example bad: "Consider exploring additional fashion sources"

=== EXAMPLE OUTPUT (for reference only) ===
{
  "summary": "Structured blazers, red formal suits, and evening wear dominate—you're clearly refining a sophisticated look. The pattern points toward an upcoming event or a deliberate style shift toward sharper, more intentional dressing. Your evening save times suggest this is how you unwind: planning what to wear next.",
  "suggestions": ["Build a mood board pairing the red suit saves with the neutral blazers", "Outline three outfit combinations from the formal wear pattern", "Create a 'next event' Focus to lock in these ideas"]
}

=== YOUR OUTPUT (JSON only) ===
{
  "summary": "your specific summary here",
  "suggestions": ["action 1 tied to specific content", "action 2 tied to specific content", "action 3 tied to specific content"]
}`;

    // DEBUG: Log prompt data for iteration
    if (import.meta.env?.DEV) {
      console.log('📊 [Weekly Summary] Prompt Data:', {
        itemCount: recentItems.length,
        topTags: topActualTopics,
        titlePatterns: topTitlePatterns,
        lateSaves,
        earlySaves,
        topDomain
      });
    }

    const response = await ai.text(summaryPrompt);
    
    // DEBUG: Log raw response
    if (import.meta.env?.DEV) {
      console.log('🤖 [Weekly Summary] Raw AI Response:', response);
    }
    
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      
      // DEBUG: Log parsed output for review
      if (import.meta.env?.DEV) {
        console.log('✅ [Weekly Summary] FINAL OUTPUT:');
        console.log('   TEASER:', teaser);
        console.log('   SUMMARY:', parsed.summary);
        console.log('   SUGGESTIONS:', parsed.suggestions);
        console.log('─────────────────────────────────────');
      }
      
      return {
        teaser: teaser.substring(0, 100),
        summary: parsed.summary || generateHardcodedFallback(weeklyCount, topActualTopics, lateSaves, topFormat),
        suggestions: parsed.suggestions && parsed.suggestions.length > 0 
          ? parsed.suggestions 
          : generateFallbackSuggestions(hasStrongCluster, strongest)
      };
    }
    
    // Hard fallback if JSON parsing fails
    return {
      teaser,
      summary: generateHardcodedFallback(weeklyCount, topActualTopics, lateSaves, topFormat),
      suggestions: generateFallbackSuggestions(hasStrongCluster, strongest)
    };
  } catch (error) {
    console.error('Weekly summary generation failed:', error);
    return {
      teaser: topTopics[0] ? `Focused on ${topTopics[0].toLowerCase()}` : 'Your patterns are forming',
      summary: weeklyCount > 0 && topTopics.length > 0
        ? `${topTopics.slice(0, 2).join(' and ')} are your main themes. The pattern suggests intentional focus rather than scattered browsing.`
        : weeklyCount > 0
          ? 'Your patterns are emerging. The saves are forming a direction.'
          : 'Start saving items to see your weekly summary.',
      suggestions: topTopics[0]
        ? [
            `Create a Focus around ${topTopics[0].toLowerCase()}`,
            'Review what connects your recent saves',
            'Save more in your top areas to deepen the pattern'
          ]
        : [
            'Save a few items that catch your attention',
            'Group similar saves into a Focus',
            'Check back soon for insights'
          ]
    };
  }
}

// Hardcoded fallback generator when AI fails
function generateHardcodedFallback(weeklyCount, topTopics, lateSaves, topFormat) {
  const hasTopics = topTopics.length > 0;
  const strongestTopic = topTopics[0]?.split(' (')[0];
  const strongestCount = topTopics[0] ? parseInt(topTopics[0].split('(')[1]) : 0;
  
  if (strongestCount >= 5 && strongestTopic) {
    return `${strongestTopic} is clearly on your mind—this concentration suggests you're building toward something specific.`;
  } else if (lateSaves >= 5) {
    return `Late-night saving (after 10pm) usually means you're processing ideas or organizing direction. That's when your real interests surface.`;
  } else if (topFormat && topFormat[1] >= 5) {
    return `You're gravitating toward ${topFormat[0] === 'image' ? 'visual' : topFormat[0]} content right now. This format preference often signals a shift in how you're processing ideas.`;
  } else if (hasTopics) {
    return `${topTopics.slice(0, 2).map(t => t.split(' (')[0]).join(' and ')} are loosely emerging. This looks like maintenance mode rather than deep exploration.`;
  } else {
    return `Scattered themes this week. When weeks look like this, it usually leads to a more coherent pattern next week.`
  }
}

// Fallback suggestions generator
function generateFallbackSuggestions(hasStrongCluster, topTheme) {
  if (hasStrongCluster && topTheme) {
    return [
      `Group your ${topTheme.toLowerCase()} saves into a Focus to see the full scope`,
      'Outline what you want to build from this collection',
      'Set a deadline to turn research into action'
    ];
  } else {
    return [
      'Want me to cluster your saves into themes for you?',
      'Create a Focus from your top theme this week',
      'Save a few more items in one area to form a stronger pattern'
    ];
  }
}

// Momentum V2: Compute metrics from items
// Uses the same unified vault data as Smart Insights
function computeMomentumMetrics(items = []) {
  if (!items || items.length === 0) {
    return {
      totalItemsThisWeek: 0,
      totalItemsLastWeek: 0,
      weekDeltaPercent: 0,
      weekDeltaText: 'No items yet',
      topTwoRisingThemes: [],
      mostActiveDay: null,
      mostActiveDayShort: null,
      leastActiveDay: null,
      peakUploadHour: null,
      dailyCounts: [0, 0, 0, 0, 0, 0, 0],
      hasEnoughData: false
    };
  }
  
  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
  
  // Handle both camelCase (vault) and snake_case (dashboard) date fields
  const getItemDate = (item) => {
    const dateStr = item.created_at || item.createdAt;
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };
  
  // Debug: Check date field access
  if (import.meta.env.DEV && items.length > 0) {
    const sampleItem = items[0];
    const sampleDate = getItemDate(sampleItem);
    console.log('🔍 [Momentum Debug] Sample item date check:', {
      hasCreatedAt: !!sampleItem.created_at,
      hasCreatedAtCamel: !!sampleItem.createdAt,
      dateStr: sampleItem.created_at || sampleItem.createdAt,
      parsedDate: sampleDate,
      itemKeys: Object.keys(sampleItem).slice(0, 10)
    });
  }
  
  const thisWeekItems = items.filter(item => {
    const createdAt = getItemDate(item);
    if (!createdAt) return false;
    return createdAt >= oneWeekAgo && createdAt <= now;
  });
  
  const lastWeekItems = items.filter(item => {
    const createdAt = getItemDate(item);
    if (!createdAt) return false;
    return createdAt >= twoWeeksAgo && createdAt < oneWeekAgo;
  });
  
  // Debug: Check date ranges and sample dates
  if (import.meta.env.DEV) {
    const itemsWithDates = items.filter(item => getItemDate(item) !== null);
    const sampleDates = itemsWithDates.slice(0, 5).map(item => ({
      date: getItemDate(item)?.toISOString(),
      isThisWeek: getItemDate(item) >= oneWeekAgo && getItemDate(item) <= now,
      isLastWeek: getItemDate(item) >= twoWeeksAgo && getItemDate(item) < oneWeekAgo
    }));
    
    console.log('🔍 [Momentum Debug] Date filtering:', {
      totalItems: items.length,
      oneWeekAgo: oneWeekAgo.toISOString(),
      twoWeeksAgo: twoWeeksAgo.toISOString(),
      now: now.toISOString(),
      thisWeekCount: thisWeekItems.length,
      lastWeekCount: lastWeekItems.length,
      itemsWithDates: itemsWithDates.length,
      itemsWithoutDates: items.filter(item => getItemDate(item) === null).length,
      sampleDates: sampleDates
    });
  }
  
  const totalThisWeek = thisWeekItems.length;
  const totalLastWeek = lastWeekItems.length;
  
  let weekDeltaPercent = 0;
  let weekDeltaText = 'No items yet';
  
  if (totalLastWeek === 0 && totalThisWeek === 0) {
    weekDeltaText = 'No items yet';
    weekDeltaPercent = 0;
  } else if (totalLastWeek === 0 && totalThisWeek > 0) {
    weekDeltaText = `From 0 to ${totalThisWeek} item${totalThisWeek !== 1 ? 's' : ''}`;
    weekDeltaPercent = 100;
  } else {
    const percent = ((totalThisWeek - totalLastWeek) / totalLastWeek) * 100;
    const sign = percent > 0 ? '+' : percent < 0 ? '−' : '';
    const rounded = Math.round(Math.abs(percent));
    weekDeltaPercent = Math.round(percent);
    weekDeltaText = `${sign}${rounded}% (${totalLastWeek} → ${totalThisWeek})`;
  }
  
  const dayCountsRaw = [0, 0, 0, 0, 0, 0, 0];
  const hourCounts = new Array(24).fill(0);
  const themeCounts = {};
  
  // Use this week's items for daily/hourly counts, but if this week is empty,
  // use last week's items so we can still show patterns
  const itemsForAnalysis = thisWeekItems.length > 0 ? thisWeekItems : lastWeekItems;
  
  itemsForAnalysis.forEach(item => {
    const createdAt = getItemDate(item);
    if (!createdAt) return;
    
    const dayOfWeek = createdAt.getDay();
    dayCountsRaw[dayOfWeek]++;
    
    const hour = createdAt.getHours();
    hourCounts[hour]++;
    
    // Handle both camelCase (vault) and snake_case (dashboard) field names
    const topics = [
      ...(item.auto_tags?.topics || item.autoTags?.topics || []),
      ...(item.meta?.ai_analysis?.topics || item.meta?.aiAnalysis?.topics || []),
      ...(item.primary_tags || item.primaryTags || []),
      ...(item.microTopics || []),
      ...(item.extractedThemes || [])
    ];
    
    topics.forEach(topic => {
      if (topic && typeof topic === 'string') {
        const theme = classifyTheme([topic.toLowerCase()]);
        if (theme && theme !== 'General') {
          themeCounts[theme] = (themeCounts[theme] || 0) + 1;
        }
      }
    });
  });
  
  const dailyCounts = [...dayCountsRaw.slice(1), dayCountsRaw[0]];
  
  const dayNamesShort = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  const dayNamesFull = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  let maxDay = 0, minDay = 0, maxCount = dailyCounts[0], minCount = dailyCounts[0];
  
  dailyCounts.forEach((count, i) => {
    if (count > maxCount) {
      maxCount = count;
      maxDay = i;
    }
    if (count < minCount) {
      minCount = count;
      minDay = i;
    }
  });
  
  let peakHour = 0, peakHourCount = 0;
  hourCounts.forEach((count, hour) => {
    if (count > peakHourCount) {
      peakHourCount = count;
      peakHour = hour;
    }
  });
  
  const sortedThemes = Object.entries(themeCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([theme]) => theme);
  
  // Show data if we have items this week OR last week (so we can show trends)
  // Also show if we have any items at all (even if spread out)
  const hasEnoughData = totalThisWeek >= 5 || totalLastWeek >= 5 || items.length >= 10;
  
  if (import.meta.env.DEV) {
    console.log('🔍 [Momentum Debug] hasEnoughData check:', {
      totalThisWeek,
      totalLastWeek,
      totalItems: items.length,
      condition1: totalThisWeek >= 5,
      condition2: totalLastWeek >= 5,
      condition3: items.length >= 10,
      hasEnoughData
    });
  }
  
  return {
    totalItemsThisWeek: totalThisWeek,
    totalItemsLastWeek: totalLastWeek,
    weekDeltaPercent,
    weekDeltaText,
    topTwoRisingThemes: sortedThemes,
    mostActiveDay: hasEnoughData ? dayNamesFull[maxDay] : null,
    mostActiveDayShort: hasEnoughData ? dayNamesShort[maxDay] : null,
    leastActiveDay: hasEnoughData ? dayNamesFull[minDay] : null,
    peakUploadHour: hasEnoughData ? peakHour : null,
    dailyCounts,
    hasEnoughData
  };
}

// Momentum V2: Generate micro AI insight
async function generateMomentumInsight(metrics) {
  if (!metrics.hasEnoughData) {
    return "";
  }
  
  try {
    const peakHour12 = (() => {
      const hour = metrics.peakUploadHour;
      if (hour === null) return 'various times';
      const period = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${hour12}:00 ${period}`;
    })();
    
    const prompt = `Write ONE sharp, specific sentence about this user's weekly activity (max 15 words).

Metrics:
- Items this week: ${metrics.totalItemsThisWeek}
- Change from last week: ${metrics.weekDeltaText}
- Most active day: ${metrics.mostActiveDay}
- Peak hour: ${peakHour12}
- Rising themes: ${metrics.topTwoRisingThemes.join(', ') || 'Various'}

RULES:
- Reference SPECIFIC themes (not "various topics" or "different areas")
- Include behavioral signals (time, day, or momentum change)
- Be direct, confident, and pattern-driven
- NO generic filler like "you explored" or "you engaged with"
- NEVER mention item counts like "14 saves" or "20 items"
- Example good output: "Momentum is up 40%—${metrics.topTwoRisingThemes[0] || 'your top themes'} dominating your ${metrics.mostActiveDay}s"
- Text only, no quotes.`;

    const response = await openaiService.createChatCompletion([
      {
        role: 'system',
        content: 'You are a sharp analyst who studies user behavior and writes specific, pattern-driven insights. One sentence only. Never generic.'
      },
      {
        role: 'user',
        content: prompt
      }
    ], {
      model: 'gpt-4o',
      temperature: 0.7,
      max_tokens: 60
    });

    if (response.error || !response.data) {
      return "";
    }

    return response.data.trim().replace(/^["']|["']$/g, '');
  } catch (error) {
    console.error('Momentum insight generation failed:', error);
    return "";
  }
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hoveredDay, setHoveredDay] = useState(null);
  
  useBehavioralLearning(user?.id);

  useEffect(() => {
    const startTime = performance.now();
    
    return () => {
      const loadTime = performance.now() - startTime;
      setTimeout(() => {
        telemetry.trackDashboardRender(
          Math.round(loadTime),
          9,
          5
        );
      }, 100);
    };
  }, []);

  const [isExpanded, setIsExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  
  const [recentItems, setRecentItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [learningRhythm, setLearningRhythm] = useState(null);
  const [curiosityData, setCuriosityData] = useState(null);
  const [topTopicsMonth, setTopTopicsMonth] = useState([]);
  const [curiosityDistribution, setCuriosityDistribution] = useState([]);
  const [swirls, setSwirls] = useState([]);
  const [recentQueries, setRecentQueries] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState("");
  const [weeklySuggestions, setWeeklySuggestions] = useState([]);
  const [weeklySummaryLoading, setWeeklySummaryLoading] = useState(true);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);
  
  const [momentumMetrics, setMomentumMetrics] = useState({
    totalItemsThisWeek: 0,
    totalItemsLastWeek: 0,
    weekDeltaPercent: 0,
    weekDeltaText: 'No items yet',
    topTwoRisingThemes: [],
    mostActiveDay: null,
    mostActiveDayShort: null,
    leastActiveDay: null,
    peakUploadHour: null,
    dailyCounts: [0, 0, 0, 0, 0, 0, 0],
    hasEnoughData: false
  });
  
  const [momentumInsight, setMomentumInsight] = useState("");
  
  const [smartSuggestions, setSmartSuggestions] = useState([]);
  const [pinnedSuggestions, setPinnedSuggestions] = useState([]);
  const [activeSuggestion, setActiveSuggestion] = useState(null);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [insightsTab, setInsightsTab] = useState('insights');
  const [userPlans, setUserPlans] = useState([]);

  useEffect(() => {
    if (import.meta.env.DEV) console.log('✅ Home mounted (Living Feed)');
    
    // Debug: Expose cache clear function
    if (import.meta.env?.DEV) {
      window.clearWeeklySummaryCache = async () => {
        try {
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          // Wait for auth session first
          const { data: session } = await supabase.auth.getSession();
          if (!session?.session?.user?.id) {
            console.error('No user logged in');
            return;
          }
          
          const authenticatedUserId = session.session.user.id;
          
          const { error } = await supabase
            .from('weekly_summary_cache')
            .delete()
            .eq('user_id', authenticatedUserId);
          
          if (error) {
            console.error('Error clearing cache:', error);
          } else {
            if (import.meta.env.DEV) console.log('✅ Weekly summary cache cleared! Reloading...');
            setTimeout(() => location.reload(), 500);
          }
        } catch (err) {
          console.error('Error:', err);
        }
      };
      if (import.meta.env.DEV) console.log('💡 Debug: Run clearWeeklySummaryCache() to force regeneration');
    }
    
    if (!FLAGS.SELF_DASHBOARD_ENABLED) {
      return;
    }

    const loadHomeData = async () => {
      if (!user?.id) return;
      
      setLoading(true);
      try {
        const unifiedData = await loadDashboardData(user.id, true);
        
        if (import.meta.env.DEV) console.log(`✅ Home data loaded:`, {
          items: unifiedData.items.length,
          swirls: unifiedData.swirls.length,
          queries: unifiedData.queries.length,
        });

        // Load user plans
        const { data: plans } = await documentService.getUserPlans(user.id);
        if (plans) {
          setUserPlans(plans);
        }

        setSwirls(unifiedData.swirls || []);
        const items = unifiedData.items || [];
        setAllItems(items);
        
        if (import.meta.env?.DEV && typeof window !== 'undefined') {
          window.__SWIRL_DEBUG_ITEMS__ = items;
          if (import.meta.env.DEV) {
            console.log('%c[Debug] Items exposed as window.__SWIRL_DEBUG_ITEMS__', 'color: #8b5cf6');
            console.log(`📊 [Momentum] Loaded ${items.length} items from unified vault`);
          }
        }

        const recent = items.slice(0, 12);
        setRecentItems(recent);

        // Compute momentum metrics from the same unified vault data
        processLearningRhythm(unifiedData.queries, items);
        processCuriosityData(unifiedData.items);

        const uniqueQueries = [];
        const seen = new Set();
        for (const q of unifiedData.queries || []) {
          const raw = q.query || q.text || q.message;
          const queryText = raw ? raw.toLowerCase().trim() : null;
          if (queryText && !seen.has(queryText)) {
            seen.add(queryText);
            uniqueQueries.push(q);
          }
          if (uniqueQueries.length === 8) break;
        }
        setRecentQueries(uniqueQueries);
        
      } catch (error) {
        console.error('Error loading home data:', error);
        setRecentItems([]);
        setSwirls([]);
        setRecentQueries([]);
      } finally {
        setLoading(false);
      }
    };

    loadHomeData();
  }, [user?.id]);

  const processLearningRhythm = (queries, items) => {
    try {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      if (import.meta.env.DEV) {
        console.log(`📊 [Momentum] Computing metrics from ${items?.length || 0} items`);
      }
      
      const metrics = computeMomentumMetrics(items || []);
      
      if (import.meta.env.DEV) {
        console.log(`📊 [Momentum] Metrics computed:`, {
          totalThisWeek: metrics.totalItemsThisWeek,
          totalLastWeek: metrics.totalItemsLastWeek,
          hasEnoughData: metrics.hasEnoughData,
          dailyCounts: metrics.dailyCounts
        });
      }
      
      setMomentumMetrics(metrics);
      
      if (queries && queries.length > 0) {
        const weeklyQueries = queries.filter(q => {
          const queryDate = new Date(q.created_at);
          return queryDate >= weekAgo;
        });

        const topics = new Set();
        weeklyQueries.forEach(query => {
          const raw = query.query || query.text || query.message;
          const terms = raw?.split(' -> ') || [];
          terms.forEach(term => {
            if (term.trim()) topics.add(term.trim().toLowerCase());
          });
        });

        setLearningRhythm({
          weeklyCount: metrics.totalItemsThisWeek || weeklyQueries.length || 0,
          topics: Array.from(topics).slice(0, 3)
        });
        return;
      }

      if (items && items.length > 0) {
        const weeklyItems = items.filter(item => {
          const itemDate = new Date(item.created_at);
          return itemDate >= weekAgo;
        });

        const topics = new Set();
        weeklyItems.forEach(item => {
          const itemTopics = item.auto_tags?.topics || [];
          itemTopics.forEach(topic => {
            if (topic) topics.add(topic.toLowerCase());
          });
        });

        setLearningRhythm({
          weeklyCount: metrics.totalItemsThisWeek || weeklyItems.length || 0,
          topics: Array.from(topics).slice(0, 3)
        });
        return;
      }

      setLearningRhythm({ weeklyCount: 0, topics: [] });
    } catch (error) {
      console.error('Error processing learning rhythm:', error);
      setLearningRhythm({ weeklyCount: 0, topics: [] });
    }
  };

  const processCuriosityData = (items) => {
    try {
      const themeCounts = {};
      
      items.forEach(item => {
        let topics = [];
        
        if (item.auto_tags && typeof item.auto_tags === 'object') {
          topics = item.auto_tags.topics || item.auto_tags.themes || [];
        }
        
        if (topics.length === 0 && item.ai_analysis && typeof item.ai_analysis === 'object') {
          topics = item.ai_analysis.topics || item.ai_analysis.tags || [];
        }
        
        topics.forEach(topic => {
          if (topic && typeof topic === 'string') {
            const normalizedTopic = topic.toLowerCase().trim();
            if (normalizedTopic.length > 0) {
              themeCounts[normalizedTopic] = (themeCounts[normalizedTopic] || 0) + 1;
            }
          }
        });
      });

      const sortedCuriosity = Object.entries(themeCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([theme, count]) => {
          const capitalized = theme.charAt(0).toUpperCase() + theme.slice(1);
          return [capitalized, count];
        });

      const topThemes = sortedCuriosity.slice(0, 6).reduce((acc, [theme, count]) => {
        acc[theme] = count;
          return acc;
        }, {});

      setCuriosityData(topThemes);
      setTopTopicsMonth(sortedCuriosity.slice(0, 3));
      setCuriosityDistribution(sortedCuriosity.slice(0, 6));
      
      return themeCounts;
    } catch (error) {
      console.error('Error processing curiosity data:', error);
      setCuriosityData({});
      setTopTopicsMonth([]);
      setCuriosityDistribution([]);
      return {};
    }
  };

  // Load cache immediately on mount, before other data is ready
  useEffect(() => {
    async function loadCachedSummary() {
      // Wait for auth session first
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user?.id) return;
      
      const authenticatedUserId = session.session.user.id;
      if (!authenticatedUserId) return;

      try {
        const cache = await getCachedWeeklySummary(authenticatedUserId);
        if (cache && isCacheValid(cache)) {
          const summaryData = getSummaryFromCache(cache);
          if (summaryData) {
            // Check if cached summary is the "no items" message - if so, don't use it
            const isNoItemsMessage = summaryData.summary?.toLowerCase().includes('no items saved') ||
                                   summaryData.teaser?.toLowerCase().includes('no items saved') ||
                                   summaryData.summary?.toLowerCase().includes('did not save');
            
            if (!isNoItemsMessage) {
              setWeeklySummary(summaryData);
              setWeeklySuggestions(summaryData.suggestions || []);
              setWeeklySummaryLoading(false);
              if (import.meta.env?.DEV) {
                console.log('✅ Loaded cached weekly summary immediately');
              }
              return;
            } else if (import.meta.env?.DEV) {
              console.log('⚠️ Cached summary is "no items" message, will regenerate when data loads');
            }
          }
        }
        // If no cache, keep loading state true until generation completes
      } catch (error) {
        console.error('Error loading cached summary:', error);
      }
    }

    loadCachedSummary();
  }, [user?.id]);

  // Generate summary in background when data is ready
  useEffect(() => {
    async function runSummary() {
      if (!user?.id) return;
      
      // Don't regenerate if we already have a valid cached summary
      if (weeklySummary && weeklySuggestions.length > 0) {
        setWeeklySummaryLoading(false);
        return;
      }

      // Wait for required data
      if (!curiosityData || !learningRhythm) {
        return;
      }

      try {
        setWeeklySummaryLoading(true);
        
        // Check cache again (in case it wasn't loaded in first effect)
        const cache = await getCachedWeeklySummary(user.id);
        const topTopics = Object.keys(curiosityData).slice(0, 5);
        const dominantTheme = getDominantTheme(topTopics);

        // If cache exists and is valid, use it (unless it's a "no items" message)
        if (cache && isCacheValid(cache)) {
          const summaryData = getSummaryFromCache(cache);
          if (summaryData) {
            // Check if cached summary is the "no items" message - regenerate if we now have items
            const isNoItemsMessage = summaryData.summary?.toLowerCase().includes('no items saved') ||
                                   summaryData.teaser?.toLowerCase().includes('no items saved') ||
                                   summaryData.summary?.toLowerCase().includes('did not save');
            
            // If cache says "no items" but we have items now, regenerate
            if (isNoItemsMessage && (learningRhythm.weeklyCount > 0 || (allItems || []).length > 0)) {
              if (import.meta.env?.DEV) {
                console.log('🔄 Regenerating: cached "no items" summary but items now available');
              }
              // Continue to generation below
            } else if (!isNoItemsMessage) {
              setWeeklySummary(summaryData);
              setWeeklySuggestions(summaryData.suggestions || []);
              setWeeklySummaryLoading(false);
              if (import.meta.env?.DEV) {
                console.log('✅ Using cached weekly summary');
              }
              return;
            }
          }
        }

        // Only check thresholds if cache is invalid or doesn't exist
        const shouldRegen = await shouldRegenerateSummary({
          userId: user.id,
          cache,
          items: allItems || [],
          currentDominantTheme: dominantTheme,
          currentTopicMap: curiosityData,
          smartSuggestions: smartSuggestions || [],
          momentumMetrics
        });

        // If cache exists but thresholds not met, use cache (unless it's "no items" message)
        if (cache && !shouldRegen) {
          const summaryData = getSummaryFromCache(cache);
          if (summaryData) {
            // Check if cached summary is the "no items" message - regenerate if we now have items
            const isNoItemsMessage = summaryData.summary?.toLowerCase().includes('no items saved') ||
                                   summaryData.teaser?.toLowerCase().includes('no items saved') ||
                                   summaryData.summary?.toLowerCase().includes('did not save');
            
            // If cache says "no items" but we have items now, regenerate
            if (isNoItemsMessage && (learningRhythm.weeklyCount > 0 || (allItems || []).length > 0)) {
              if (import.meta.env?.DEV) {
                console.log('🔄 Regenerating: cached "no items" summary but items now available');
              }
              // Continue to generation below
            } else if (!isNoItemsMessage) {
              setWeeklySummary(summaryData);
              setWeeklySuggestions(summaryData.suggestions || []);
              setWeeklySummaryLoading(false);
              if (import.meta.env?.DEV) {
                console.log('✅ Using cached weekly summary (thresholds not met)');
              }
              return;
            }
          }
        }

        // Generate new summary only if no cache or thresholds met
        if (import.meta.env?.DEV) {
          console.log(shouldRegen ? '🔄 Regenerating weekly summary (threshold triggered)' : '📝 Generating new weekly summary (no cache)');
        }

        // Generate summary with actual items
        const result = await generateAISummary({
          topTopics,
          weeklyCount: learningRhythm.weeklyCount || 0,
          topicMap: curiosityData,
          dominantTheme,
          items: allItems || []
        });

        // Save to cache (gracefully handle if table doesn't exist)
        try {
          await saveWeeklySummaryCache({
            userId: user.id,
            summaryData: result,
            themesUsed: topTopics,
            dominantTheme,
            itemCount: learningRhythm.weeklyCount || 0,
            meta: {
              topTopics,
              weeklyCount: learningRhythm.weeklyCount || 0,
              generatedAt: new Date().toISOString()
            }
          });
        } catch (cacheError) {
          // If cache save fails (e.g., table doesn't exist), continue anyway
          if (import.meta.env?.DEV) {
            console.warn('Could not save to cache (table may not exist yet):', cacheError);
          }
        }

        setWeeklySummary(result);
        setWeeklySuggestions(result.suggestions || []);
        setWeeklySummaryLoading(false);
      } catch (error) {
        console.error('Error in weekly summary generation:', error);
        // Fallback to empty state
        setWeeklySummary("");
        setWeeklySuggestions([]);
        setWeeklySummaryLoading(false);
      }
    }

    // Run in background without blocking
    runSummary();
  }, [user?.id, curiosityData, learningRhythm, allItems, smartSuggestions, momentumMetrics, weeklySummary]);

  useEffect(() => {
    async function runMomentumInsight() {
      if (!momentumMetrics.hasEnoughData) {
        setMomentumInsight("");
        return;
      }
      
      const insight = await generateMomentumInsight(momentumMetrics);
      setMomentumInsight(insight);
    }

    runMomentumInsight();
  }, [momentumMetrics]);

  useEffect(() => {
    async function loadSuggestions() {
      if (!user?.id) {
        setSmartSuggestions([]);
        setSuggestionsLoading(false);
        return;
      }

      try {
        setSuggestionsLoading(true);
        
        const forceRefresh = import.meta.env?.DEV || false;
        if (import.meta.env.DEV) {
          console.log('%c[Home] Loading Smart Insights...', 'color: #8b5cf6; font-weight: bold');
          console.log(`  Items available: ${allItems?.length || 0}`);
          console.log(`  Force refresh: ${forceRefresh}`);
        }
        
        const suggestions = await suggestionsService.getSuggestionsForUser(
          user.id,
          allItems || [],
          momentumMetrics,
          forceRefresh
        );
        
        setSmartSuggestions(suggestions || []);
        
        const pinned = await suggestionsService.getPinnedSuggestions(user.id);
        setPinnedSuggestions(pinned);
      } catch (error) {
        console.error('Failed to load suggestions:', error);
        setSmartSuggestions([]);
      } finally {
        setSuggestionsLoading(false);
      }
    }

    loadSuggestions();
  }, [user?.id, allItems, momentumMetrics]);

  // Refresh smart insights (called after actions like archiving a focus)
  const refreshSmartInsights = async () => {
    if (!user?.id) return;
    
    try {
      setSuggestionsLoading(true);
      const suggestions = await suggestionsService.getSuggestionsForUser(
        user.id,
        allItems || [],
        momentumMetrics,
        true // Force refresh
      );
      setSmartSuggestions(suggestions || []);
      
      const pinned = await suggestionsService.getPinnedSuggestions(user.id);
      setPinnedSuggestions(pinned);
      
      console.log('✅ Smart Insights refreshed');
    } catch (error) {
      console.error('Failed to refresh suggestions:', error);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  if (!FLAGS.SELF_DASHBOARD_ENABLED) {
    return (
      <div className="min-h-screen bg-[#F8F8F9] flex items-center justify-center">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center">
          <h1 className="text-3xl font-semibold text-neutral-900 mb-3">Home</h1>
          <p className="mt-3 text-neutral-600">
            This feature is disabled. Set <code className="px-2 py-1 bg-neutral-100 rounded">VITE_SELF_DASHBOARD_ENABLED=true</code> to preview.
          </p>
        </div>
      </div>
    );
  }

  const themeCards = swirls.slice(0, 2).map((swirl, idx) => ({
    id: swirl.id || `theme-${idx}`,
    title: swirl.title || `Theme ${idx + 1}`,
    topics:
      (Array.isArray(swirl.topics) && swirl.topics.length > 0
        ? swirl.topics.slice(0, 2)
        : []) || [],
    data: swirl
  }));

  // Function to open content modal (for reuse)
  const openContentModal = (item, i) => {
    if (user?.id && item.id) {
      contentService.trackItemView(item.id, user.id).catch(() => {});
      personalizationService.updateContextFromItem(item).catch(() => {});
    }
    
    const mappedItem = {
      ...item,
      id: item.id,
      title: item.title || item.description || 'Untitled',
      description: item.description,
      url: item.url,
      source_type: item.source_type || item.content_type,
      contentType: item.source_type || item.content_type,
      channel: item.channel || item.meta?.channel || (item.source_type ? item.source_type.charAt(0).toUpperCase() + item.source_type.slice(1) : 'Content'),
      thumbnail_url: item.thumbnail_url || item.url || item.image_url,
      analysis_data: item.analysis_data || item.meta?.analysis_data || {
        summary: item.meta?.summary || item.description,
        ocr: { text: item.extracted_text },
        colors: { primaryColor: item.primary_color, mood: item.color_mood, palette: item.color_palette },
        tagging: { primaryTags: item.primary_tags, allTags: item.auto_tags },
      },
      extracted_text: item.extracted_text,
      auto_tags: item.auto_tags,
      primary_tags: item.primary_tags,
      primary_color: item.primary_color,
      color_mood: item.color_mood,
      color_palette: item.color_palette,
      meta: item.meta,
      created_at: item.created_at,
      _source: item._source || (item.source_type === 'image' ? 'deepsearch.items' : 'instagram_content'),
    };
    
    setSelectedContent(mappedItem);
    setShowContentModal(true);
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
      <Sidebar
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded(!isExpanded)}
      />

      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="fixed left-16 top-8 w-8 h-8 bg-white/80 backdrop-blur-xl rounded-r-xl flex items-center justify-center text-gray-600 hover:bg-white/90 transition-all duration-300 z-[60] shadow-lg border border-white/20"
        >
          <ChevronRight size={16} />
        </button>
      )}

      <main 
        className={`flex-1 overflow-y-auto transition-all duration-500 ease-out ${isExpanded ? 'ml-72' : 'ml-16'}`} 
        style={{ minHeight: '100vh' }}
      >
        <div id="home-content">
          <div className="min-h-screen p-5" style={{ backgroundColor: '#F7F7F8' }}>
            <div className="max-w-6xl mx-auto space-y-5">

              <HomeHeader />

              {/* Main Grid: Left Column (2/3) + Right Column (1/3) */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* LEFT COLUMN: Weekly Summary + Your Recent Focus & Plans */}
                <div className="lg:col-span-2 space-y-8">
                  <WeeklySummary 
                    weeklySummary={weeklySummary}
                    loading={weeklySummaryLoading} 
                    onOpenModal={() => setShowSummaryModal(true)} 
                  />

                  {/* Smart Insights */}
                  <SmartInsights
                    smartSuggestions={smartSuggestions}
                    pinnedSuggestions={pinnedSuggestions}
                    suggestionsLoading={suggestionsLoading}
                    insightsTab={insightsTab}
                    onTabChange={setInsightsTab}
                    onSuggestionClick={(suggestion) => {
                      setActiveSuggestion(suggestion);
                      setShowSuggestionModal(true);
                    }}
                    onUnpin={setPinnedSuggestions}
                    userId={user?.id}
                  />

                  {/* Coming Back to You */}
                  <MemorySurfacing />

                  {/* Recent Focuses */}
                  <Focuses />

                  {/* Recently Saved Items */}
                  <RecentlySaved recentItems={recentItems} onItemClick={openContentModal} />
                </div>

                {/* RIGHT COLUMN: Your Momentum + Where Your Curiosity Lives */}
                <div className="space-y-8">
                  <Momentum
                    momentumMetrics={momentumMetrics}
                    learningRhythm={learningRhythm}
                    momentumInsight={momentumInsight}
                    hoveredDay={hoveredDay}
                    onHoverDay={setHoveredDay}
                  />

                  {/* Where Your Curiosity Lives */}
                  {curiosityData && Object.keys(curiosityData).length > 0 && (() => {
                    const sortedEntries = Object.entries(curiosityData).sort((a, b) => b[1] - a[1]);
                    const curiosityDistribution = sortedEntries.slice(0, 3);
                    const total = Object.values(curiosityData).reduce((a, b) => a + b, 0);
                    return (
                      <div className="bg-white rounded-[22px] p-6" style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
                        <h3 className="text-base font-semibold mb-5 tracking-tight" style={{ color: '#111111' }}>Where Your Curiosity Lives</h3>
                        <div className="space-y-3">
                          {curiosityDistribution.map(([topic, count], i) => {
                            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                            if (!topic || pct === 0 || isNaN(pct)) return null;
                            return (
                              <div key={topic}>
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-[12px] font-medium" style={{ color: '#111111' }}>{topic}</span>
                                  <span className="text-[11px] font-medium tabular-nums" style={{ color: '#999999' }}>{pct}%</span>
                                </div>
                                <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#F0F0F0' }}>
                                  <div 
                                    className="h-full rounded-full"
                                    style={{ 
                                      width: `${Math.max(0, Math.min(100, pct))}%`,
                                      backgroundColor: '#7C3AED'
                                    }}
                                  />
                                </div>
                              </div>
                            );
                          }).filter(Boolean)}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Continue With These Themes */}
                  <ContinueThemes swirls={swirls} items={allItems} />

                  {/* Your Creative Drafts */}
                  <Plans hasPlans={userPlans.length > 0} plans={userPlans} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <ContentDetailModal
        isOpen={showContentModal}
        onClose={() => {
          setShowContentModal(false);
          setSelectedContent(null);
        }}
        content={selectedContent}
      />

      <SmartSuggestionModal
        isOpen={showSuggestionModal}
        onClose={() => {
          setShowSuggestionModal(false);
          setActiveSuggestion(null);
        }}
        suggestion={activeSuggestion}
        allSuggestions={smartSuggestions}
        relatedItems={activeSuggestion?.relatedItems || []}
        momentumData={momentumMetrics}
        isPinned={activeSuggestion ? pinnedSuggestions.some(p => p.id === activeSuggestion.id) : false}
        onPin={async (suggestion) => {
          const newPinned = await suggestionsService.pinSuggestion(suggestion, user?.id);
          setPinnedSuggestions(newPinned);
          setShowSuggestionModal(false);
          setActiveSuggestion(null);
          setInsightsTab('pinned'); // Switch to pinned tab after pinning
        }}
        onUnpin={async (suggestionId) => {
          const newPinned = await suggestionsService.unpinSuggestion(suggestionId, user?.id);
          setPinnedSuggestions(newPinned);
          setShowSuggestionModal(false);
          setActiveSuggestion(null);
        }}
        onSnooze={(suggestionId, duration) => {
          suggestionsService.snoozeSuggestion(suggestionId, duration);
          setSmartSuggestions(prev => prev.filter(s => s.id !== suggestionId));
          setShowSuggestionModal(false);
          setActiveSuggestion(null);
        }}
        onDismiss={(suggestionId) => {
          suggestionsService.dismissSuggestion(suggestionId);
          setSmartSuggestions(prev => prev.filter(s => s.id !== suggestionId));
          setShowSuggestionModal(false);
          setActiveSuggestion(null);
        }}
        onSelectSuggestion={(suggestion) => {
          setActiveSuggestion(suggestion);
        }}
        onRefresh={refreshSmartInsights}
      />

      {showSummaryModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowSummaryModal(false)}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          />
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden"
          >
            <div className="relative h-24 bg-gradient-to-br from-purple-100 via-pink-50 to-cyan-50">
              <div 
                className="absolute inset-0 bg-[url('/assets/images/insights%20image%20header.png')] bg-cover opacity-20"
                style={{ backgroundPosition: '-20% center' }}
              />
              <div className="absolute bottom-4 left-6">
                <h2 className="text-2xl font-serif text-[#7A47F5] tracking-tight">Your Week in Review</h2>
              </div>
              <button
                onClick={() => setShowSummaryModal(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/60 hover:bg-white/80 flex items-center justify-center transition-colors shadow-sm"
              >
                <span className="text-neutral-600 text-lg">×</span>
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-6">
                <p className="text-neutral-700 leading-relaxed">
                  {typeof weeklySummary === 'object' ? weeklySummary.summary : weeklySummary}
                </p>
              </div>
              
              {weeklySuggestions.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide mb-3">
                    Suggestions for Next Week
                  </h3>
                  <div className="space-y-2">
                    {weeklySuggestions.map((suggestion, i) => (
                      <div 
                        key={i}
                        className="flex items-start gap-3 p-3 rounded-xl bg-neutral-50 hover:bg-neutral-100 transition-colors"
                      >
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center flex-shrink-0">
                          <Sparkles size={12} className="text-white" />
                        </div>
                        <p className="text-sm text-neutral-700 leading-relaxed">{suggestion}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {learningRhythm && (
                <div className="mt-6 pt-4 border-t border-neutral-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-500">Items explored this week</span>
                    <span className="font-semibold text-purple-600">{learningRhythm.weeklyCount || 0}</span>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

